import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../Context/authContext';
import { getIntercomPositionForPath } from '../helpers/intercomPosition';

const SCRIPT_ID = 'intercom-widget-script';
let bootedUserId = null;

const getAppId = () => process.env.REACT_APP_INTERCOM_APP_ID;

const buildPayload = (data, pathname) => {
  const payload = {
    app_id: getAppId(),
    user_id: String(data.id),
    email: data.email,
    name: data.fullName,
    ...getIntercomPositionForPath(pathname),
  };

  if (data.intercomUserJwt) {
    payload.intercom_user_jwt = data.intercomUserJwt;
  }

  return payload;
};

const loadIntercomScript = appId => {
  if (window.Intercom || document.getElementById(SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://widget.intercom.io/widget/${appId}`;
  document.body.appendChild(script);
};

const shutdownIntercom = () => {
  if (!window.Intercom) return;
  window.Intercom('shutdown');
  bootedUserId = null;
};

const bootIntercom = payload => {
  if (!window.Intercom) return;

  const userId = payload.user_id;

  if (bootedUserId && bootedUserId !== userId) {
    shutdownIntercom();
  }

  window.intercomSettings = payload;
  window.Intercom('boot', payload);
  bootedUserId = userId;
};

const waitForIntercom = (onReady, timeoutMs = 10000) => {
  if (window.Intercom) {
    onReady();
    return () => {};
  }

  const interval = setInterval(() => {
    if (window.Intercom) {
      clearInterval(interval);
      onReady();
    }
  }, 200);

  const timeout = setTimeout(() => clearInterval(interval), timeoutMs);

  return () => {
    clearInterval(interval);
    clearTimeout(timeout);
  };
};

const applyIntercomPosition = pathname => {
  if (!window.Intercom) return;
  window.Intercom('update', getIntercomPositionForPath(pathname));
};

export const useIntercom = () => {
  const { pathname } = useLocation();
  const { isLoggedIn, user } = useContextHook(AuthContext, v => ({
    isLoggedIn: v.isLoggedIn,
    user: v.user,
  }));

  const bootedForUserRef = useRef(null);

  useEffect(() => {
    if (!isLoggedIn) {
      shutdownIntercom();
      bootedForUserRef.current = null;
      return;
    }

    const appId = getAppId();
    if (!appId) return;

    const data = user?.data ?? {};
    if (!data.id || !data.email) return;

    const payload = buildPayload(data, pathname);
    const userId = String(data.id);

    window.intercomSettings = payload;
    loadIntercomScript(appId);

    const init = () => bootIntercom(payload);
    const cleanupWait = waitForIntercom(init);

    bootedForUserRef.current = userId;

    return () => {
      cleanupWait();
    };
  }, [isLoggedIn, user?.data?.id, user?.data?.email, user?.data?.fullName, user?.data?.intercomUserJwt]);

  useEffect(() => {
    if (!isLoggedIn || !bootedForUserRef.current) return;
    applyIntercomPosition(pathname);
  }, [pathname, isLoggedIn]);
};
